"use client";

import { type RefObject, useRef, useState } from "react";

type Band = { from: number; to: number };
type HoverPoint = { x: number; y: number; t: number };

/**
 * A drag-to-select-a-range gesture over an element, plus a hover readout when
 * idle. Used for both zoom gestures (the bar's margin and the overview lane):
 * pointer-capture means the drag keeps tracking outside the element. `toTime`
 * maps a 0–1 ratio to a timestamp; `onCommit` receives the selected range on
 * release. Returns the in-progress `band`, the `hover` point, a `clearHover`,
 * and the handlers to spread onto the capture element.
 */
export function useRubberBand({
  rectRef,
  toTime,
  onCommit,
  disabled = false,
  canHover = () => true,
}: {
  rectRef: RefObject<HTMLElement | null>;
  toTime: (ratio: number) => number;
  onCommit: (from: number, to: number) => void;
  disabled?: boolean;
  /** Suppress the idle hover readout (e.g. while another drag owns the pointer). */
  canHover?: () => boolean;
}) {
  const [band, setBand] = useState<Band | null>(null);
  const [hover, setHover] = useState<HoverPoint | null>(null);
  const bandRef = useRef<Band | null>(null);
  const dragging = useRef(false);

  const clearHover = () => setHover(null);

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (disabled) return;
    const rect = rectRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    event.preventDefault();
    setHover(null);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragging.current = true;
    const t = toTime(Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)));
    bandRef.current = { from: t, to: t };
    setBand(bandRef.current);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const rect = rectRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const t = toTime(ratio);
    if (dragging.current && bandRef.current) {
      bandRef.current = { from: bandRef.current.from, to: t };
      setBand({ ...bandRef.current });
    } else if (!dragging.current && canHover()) {
      setHover({ x: event.clientX - rect.left, y: event.clientY - rect.top, t });
    }
  };

  const onPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    if (!dragging.current) return;
    dragging.current = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const b = bandRef.current;
    bandRef.current = null;
    setBand(null);
    if (b) onCommit(b.from, b.to);
  };

  const onPointerLeave = () => setHover(null);

  return {
    band,
    hover,
    clearHover,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerLeave },
  };
}
